// Canvas renderer for the top status strip: difficulty / errors / time
// columns and the pause button on the right. Pure rendering — the caller
// passes a StatusBarRenderState every frame so settings changes apply on
// the next paint without explicit reset.
//
// The strip is the primary mobile entry point to the pause menu. Desktop
// also routes to it (in addition to the Escape key), so the canvas has a
// single visible way back to MainMenu on every device.

import { roundedRect } from '@bn-games/render-helpers';
import {
  PAUSE_BUTTON_SIZE_PX,
  PAUSE_GLYPH_BAR_HEIGHT_PX,
  PAUSE_GLYPH_BAR_WIDTH_PX,
  PAUSE_GLYPH_GAP_PX,
  STATUS_BAR_COLUMN_GAP_PX,
  STATUS_BAR_HORIZONTAL_PADDING_PX,
  STATUS_BAR_LABEL_FONT_PX,
  STATUS_BAR_VALUE_FONT_PX,
} from '../utils/constants.js';
import type { StatusBarRegion } from '../layout/types.js';
import type { Theme } from '../settings/themes.js';

type Ctx = CanvasRenderingContext2D;

const FONT_FAMILY = '"Segoe UI", system-ui, -apple-system, sans-serif';

/** One stat column: small grey caption above a larger value. */
export interface StatColumn {
  readonly label: string;
  readonly value: string;
}

export interface StatusBarRenderState {
  readonly region: StatusBarRegion;
  readonly theme: Theme;
  /** Left-to-right column list. Three columns is the design target; the
   *  renderer happily paints whatever it's given. */
  readonly columns: readonly StatColumn[];
  /** True while the pause menu is open — the pause glyph stays the same
   *  (pause-icon, not a play-arrow swap) so the button reads as "the menu
   *  entry" rather than "the next thing to do". */
  readonly paused: boolean;
  /** Subtle press feedback when a tap is in flight. */
  readonly pressedPause: boolean;
}

export class StatusBarRenderer {
  draw(ctx: Ctx, state: StatusBarRenderState): void {
    const { region } = state;
    ctx.save();
    drawColumns(ctx, state);
    drawPauseButton(ctx, region.pauseButton, state);
    ctx.restore();
  }
}

function drawColumns(ctx: Ctx, state: StatusBarRenderState): void {
  const { region, theme, columns } = state;
  if (columns.length === 0) return;

  // Columns share the room between the left padding and the pause button.
  const left = region.x + STATUS_BAR_HORIZONTAL_PADDING_PX;
  const right = region.pauseButton.x - STATUS_BAR_COLUMN_GAP_PX;
  const usable = Math.max(0, right - left);
  const colWidth = Math.floor(
    (usable - STATUS_BAR_COLUMN_GAP_PX * (columns.length - 1)) / columns.length,
  );
  if (colWidth <= 0) return;

  ctx.textAlign = 'left';
  for (let i = 0; i < columns.length; i += 1) {
    const col = columns[i];
    if (col === undefined) continue;
    const x = left + i * (colWidth + STATUS_BAR_COLUMN_GAP_PX);
    drawStatColumn(ctx, x, region.y, region.height, colWidth, col, theme);
  }
}

function drawStatColumn(
  ctx: Ctx,
  x: number,
  y: number,
  height: number,
  width: number,
  col: StatColumn,
  theme: Theme,
): void {
  // Vertically center the label+value pair inside the strip.
  const blockHeight = STATUS_BAR_LABEL_FONT_PX + 4 + STATUS_BAR_VALUE_FONT_PX;
  const top = y + Math.floor((height - blockHeight) / 2);

  // The strip sits in the dark wood tray well — read against it with the
  // same numpadDigit / numpadDigitDisabled tokens the numpad glyphs use.
  ctx.fillStyle = theme.numpadDigitDisabled;
  ctx.font = `500 ${STATUS_BAR_LABEL_FONT_PX}px ${FONT_FAMILY}`;
  ctx.textBaseline = 'top';
  ctx.fillText(col.label, x, top, width);

  ctx.fillStyle = theme.numpadDigit;
  ctx.font = `700 ${STATUS_BAR_VALUE_FONT_PX}px ${FONT_FAMILY}`;
  ctx.fillText(col.value, x, top + STATUS_BAR_LABEL_FONT_PX + 4, width);
}

function drawPauseButton(
  ctx: Ctx,
  rect: { x: number; y: number; width: number; height: number },
  state: StatusBarRenderState,
): void {
  // Accent-colored pill on the dark tray so the entry-to-menu reads at a
  // glance. Pressed state dims slightly.
  const radius = Math.floor(PAUSE_BUTTON_SIZE_PX / 2);
  const fill = state.pressedPause
    ? state.theme.numpadButtonPressed
    : state.theme.numpadButtonActive;
  ctx.fillStyle = fill;
  roundedRect(ctx, rect.x, rect.y, rect.width, rect.height, radius);
  ctx.fill();

  // Two thin bars — universal pause glyph. Centered within the circular face.
  ctx.fillStyle = state.theme.numpadButton;
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const barW = PAUSE_GLYPH_BAR_WIDTH_PX;
  const barH = PAUSE_GLYPH_BAR_HEIGHT_PX;
  const gap = PAUSE_GLYPH_GAP_PX;
  ctx.fillRect(cx - gap / 2 - barW, cy - barH / 2, barW, barH);
  ctx.fillRect(cx + gap / 2, cy - barH / 2, barW, barH);
}
